/**
 * Route paths, platform-aware.
 *
 * The browser address bar is part of "the staging site" an underwriter reviews,
 * and `/straight-bet`, `/openbet` and `/callbet` all read as sportsbook URLs.
 * These are pure React Router paths — verified: the backend contains no
 * reference to them — so they are safe to rename on the frontend alone.
 *
 * Two rules make the rename non-breaking:
 *   1. BOTH the legacy and the compliant path stay registered on the router
 *      (see `routePaths`), so old bookmarks, shared links and browser history
 *      keep resolving instead of 404-ing.
 *   2. The all-sports board's internal id, `"A-Z Betting"`, is sent verbatim
 *      to the odds API as `sport_key`, so the id itself never changes. Only
 *      its URL segment does: DTS links use the neutral `ALL_SPORTS_SLUG`, and
 *      `sportFromSlug()` maps it back to the id when the page reads the URL.
 *      The old segment still resolves, so existing links keep working.
 *
 * PPH keeps the original paths exactly; only DTS builds serve the new ones.
 * Build every link with the helpers below rather than a string literal —
 * scripts/compliance-wordscan.js rejects hardcoded legacy paths.
 */
import { isDTS } from "../utility/platform";

/** Original pay-per-head paths. Still routable on DTS for backwards compatibility. */
export const LEGACY_ROUTES = {
  straightBet: "/straight-bet",
  openBet: "/openbet",
  callBet: "/callbet",
};

/** Sweepstakes-compliant paths shown in the DTS address bar. */
export const DTS_ROUTES = {
  straightBet: "/straight-entry",
  openBet: "/open-entries",
  callBet: "/call-entries",
};

/** The paths this build links to. */
export const ROUTES = isDTS() ? DTS_ROUTES : LEGACY_ROUTES;

/**
 * Every path a route should answer on: the legacy one plus the compliant one.
 * Pass to <Route path={...}> so a DTS deploy still honours old `/openbet` links.
 */
export const routePaths = (name, suffix = "") => {
  const paths = [LEGACY_ROUTES[name], DTS_ROUTES[name]];
  return [...new Set(paths)].map((p) => `${p}${suffix}`);
};

/** Build a straight-bet URL, preserving the sport id segment verbatim. */
export const straightBetPath = (id) =>
  id === undefined || id === null || id === ""
    ? ROUTES.straightBet
    : `${ROUTES.straightBet}/${id}`;

/**
 * Id of the all-sports board. It is a UI mode rather than a sport: the page
 * sends it to the odds endpoint as `sport_key` to mean "every sport", which is
 * why the value is fixed. It is never displayed.
 */
export const ALL_SPORTS_KEY = "A-Z Betting"; // compliance-ok: odds-API mode key, never rendered

/** Id (and URL segment) of the live board — also a UI mode, not a sport. */
export const LIVE_BOARD_KEY = "livemobius";

const DTS_ALL_SPORTS_SLUG = "all-sports";

/** URL segment for the all-sports board on this build. PPH keeps the original. */
export const ALL_SPORTS_SLUG = isDTS() ? DTS_ALL_SPORTS_SLUG : ALL_SPORTS_KEY;

const safeDecode = (segment) => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment; // already decoded, or a stray "%"
  }
};

/**
 * Sport id for a board URL segment. The neutral all-sports slug maps back to
 * the internal key on every build, so a DTS link opened on PPH still works;
 * every other segment is the sport id itself.
 */
export const sportFromSlug = (segment) => {
  if (segment === undefined || segment === null || segment === "") return segment;
  const decoded = safeDecode(String(segment));
  return decoded === DTS_ALL_SPORTS_SLUG ? ALL_SPORTS_KEY : decoded;
};

/** URL of a sport's board. The all-sports board gets this build's slug. */
export const sportBoardPath = (sport) =>
  straightBetPath(
    encodeURIComponent(sport === ALL_SPORTS_KEY ? ALL_SPORTS_SLUG : sport)
  );

/** URL of the all-sports board — the logged-in home screen. */
export const allSportsPath = () => sportBoardPath(ALL_SPORTS_KEY);

/** URL of the live board. */
export const liveBoardPath = () => straightBetPath(LIVE_BOARD_KEY);

/**
 * Favorites as a filter of the all-sports board (same cards / betting),
 * not a separate moneyline-only page.
 */
export const favoritesBoardPath = () => ({
  pathname: allSportsPath(),
  search: "?filter=favorites",
  state: { selectedSport: ALL_SPORTS_KEY, boardFilter: "favorites" },
});

const stripTrailingSlash = (pathname) =>
  pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;

/** True when `pathname` is the named route on either its legacy or compliant path. */
export const isRoutePath = (pathname, name) =>
  routePaths(name).includes(stripTrailingSlash(String(pathname || "")));

/**
 * Sport id shown by a board URL: ALL_SPORTS_KEY, LIVE_BOARD_KEY or a sport
 * name. "" for the bare board path (the page redirects it to all sports), and
 * null when the pathname is not a board URL at all. Active-link logic should
 * compare these ids, never raw paths, so it holds for both path families and
 * for encoded and decoded segments alike.
 */
export const boardSportFromPath = (pathname) => {
  const match = /^(\/[^/]+)(?:\/([^/]*))?\/?$/.exec(String(pathname || ""));
  if (!match || !routePaths("straightBet").includes(match[1])) return null;
  return match[2] ? sportFromSlug(match[2]) : "";
};

/** Resolve a board route for page initialization; a bare board URL means all sports. */
export const resolveBoardSportFromPath = (pathname) => {
  const sport = boardSportFromPath(pathname);
  return sport === "" ? ALL_SPORTS_KEY : sport;
};

export default ROUTES;
